// @flow
import React from 'react';
import { connect } from 'react-redux';
import { router } from '@router';
import { get } from 'lodash';
import { openModal } from '@context/modals';
import {
  deletePatient, getPatient, showPatientPDF, resetLoadingState, patientActions, selectIsFetchingPatient, selectCanRetryPatient,
} from '@context/patients';
import { PatientProfile as PatientProfileView } from '@views';
import { mapPermissionTo } from '@helpers/connect';
import { isPresent, UserNotifications } from '@helpers/helpers';
import type { Dispatch, Patient, Doctors, Clinic, PermissionTo } from '@types';


type Props = {
  dispatch: Function,
  patient: Patient,
  clinic: Clinic,
  patientId: number,
  id: number,
  doctors: Doctors,
  isPatientLoading: boolean,
  isFetchingPatient: boolean,
  isPatientSaving: boolean,
  isPatientPrinting: boolean,
  getFullPatientError: boolean,
  canRetryPatient: boolean,
  permissionTo : PermissionTo,
  accessToken: String,
  userId: number,
};

class PatientProfile extends React.Component<Props> {
  componentWillMount() {
    const {
      patient, dispatch, isFetchingPatient, patientId,
    } = this.props;

    dispatch(resetLoadingState());

    if (isPresent(get(patient, 'deletedAt'))) {
      this.props.dispatch(router.pushPath('patientDeleted', { id: patientId }));
    }

    // Only a fetch of this very patient counts (the props were read before the reset above). With
    // the shared isLoading, a patient list still loading skipped this fetch: a blank page for good.
    // The global getFullPatientError no longer blocks it either: one failed patient blocked them all.
    if (!isFetchingPatient) {
      dispatch(getPatient(patientId));
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (isPresent(get(nextProps.patient, 'deletedAt'))) {
      this.props.dispatch(router.pushPath('patientDeleted', { id: nextProps.patientId }));
    }
    const isFull = get(nextProps, ['patient', 'full'], false);
    const { dispatch, isPatientLoading } = this.props;
    // After a failed fetch (its own, or one it waited for), try once more for this patient. Every
    // re-render while the error stayed (the error toast is one) used to fire it again, without end.
    // Not after a 4xx: a 404 (a patient of another clinic) answered the same again, on the patient list.
    if (nextProps.getFullPatientError && nextProps.canRetryPatient && !isFull && !isPatientLoading
      && this.retriedPatientId !== nextProps.patientId) {
      this.retriedPatientId = nextProps.patientId;
      dispatch(getPatient(nextProps.patientId));
    }
  }

  render() {
    const {
      patient,
      doctors,
      clinic,
      isPatientSaving,
      isPatientPrinting,
      permissionTo,
      isPatientLoading,
      accessToken,
      userId,
    } = this.props;

    return (
      <PatientProfileView
        patient={patient}
        doctors={doctors}
        clinic={clinic}
        accessToken={accessToken}
        userId={userId}
        onDeleteButtonClick={this.handleDeleteButtonClick}
        onEditPatientClick={this.handleEditPatientClick}
        isPatientSaving={isPatientSaving}
        isPatientPrinting={isPatientPrinting}
        onPrintClick={this.handlePrintClick}
        patientFormSelectDoctor={permissionTo.patientFormSelectDoctor}
        onRefresh={this.handleRefreshButtonClick}
        isLoading={isPatientLoading}
        onDeletePatientProfileImage={this.handleDeletePatientProfileImage}
        onSelectPatientProfileImage={this.handleSelectPatientProfileImage}
      />
    );
  }

  handleSelectPatientProfileImage = (file: Object, fileType: string) => {
    const patientId = get(this.props, ['patient', 'id']);
    this.props.dispatch(patientActions.uploadPatientProfileImage(patientId, file, fileType));
  };

  handleDeletePatientProfileImage = () => {
    const patientId = get(this.props, ['patient', 'id']);
    const imageId = get(this.props, ['patient', 'profileImage', 'id']);
    this.props.dispatch(patientActions.deletePatientProfileImage(patientId, imageId));
  };

  handleEditPatientClick = () => {
    const { id, dispatch, patient } = this.props;
    if (patient.full) {
      dispatch(openModal('PatientFormModal', { id }));
    } else {
      dispatch(getPatient(id, true));
    }
  };
  handleDeleteButtonClick = () => {
    const { dispatch, id } = this.props;

    UserNotifications.confirmI18n('alert/delete_patient_question', 'warning', '#FFA433', true)
      .then((value) => {
        if (value) {
          dispatch(deletePatient(id, () => {
            dispatch(router.pushPath('patientsList'));
          }));
        }
      });
  };

  handleRefreshButtonClick = () => {
    const { dispatch, patientId } = this.props;
    dispatch(getPatient(patientId));
  };

  handlePrintClick = () => {
    const { dispatch, id } = this.props;
    dispatch(showPatientPDF(id));
  }
}

const mapStateToProps = (state, ownProps: Object) => ({
  patient: get(state.context, ['patients', 'data', ownProps.match.params.id]),
  getFullPatientError: get(state.context, ['patients', 'getFullPatientError'], false),
  canRetryPatient: selectCanRetryPatient(state, ownProps.match.params.id),
  patientId: get(ownProps, ['match', 'params', 'id']),
  isPatientLoading: get(state, ['context', 'patients', 'isLoading'], false),
  isFetchingPatient: selectIsFetchingPatient(state, ownProps.match.params.id),
  isPatientSaving: get(state, ['context', 'patients', 'isSaving'], false),
  isPatientPrinting: get(state, ['context', 'patients', 'isPrinting'], false),
  doctors: state.context.doctors.data,
  clinic: get(state, ['context', 'clinics', 'activeClinic'], {}),
  id: get(ownProps, ['match', 'params', 'id']),
  permissionTo: mapPermissionTo(state),
  accessToken: get(state, ['auth', 'token']),
  userId: get(state, ['context', 'user', 'id']),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({ dispatch });

export default connect<any, any, any, any, any, any>(
  mapStateToProps,
  mapDispatchToProps,
)(PatientProfile);
